import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Loader2, AlertCircle } from 'lucide-react'
import { getPlate } from '../api/brainer'
import { PlateLayout } from './PlateLayout'
import { useSetPlateChrome } from '../store/plateChrome'
import type { BPlateInputParam } from '../types'

// Generic "render this one Plate, full stop" page — no builder chrome (no back-to-builder arrow,
// no run history, no manual Run button): the plate auto-runs on load, same as OpenPlace's own
// simplified viewer. Any per-module workspace (OpenPlace, Garage, and future ones) that wants a
// clean end-user-facing plate view mounts this at "<workspace>/plates/:id" — PlateViewRenderer's
// own navigateTo() mirrors whatever "<workspace>/plates/:id" prefix it's currently under when a
// card/row-action link forwards to another plate, so no per-module wiring is needed beyond
// mounting this component at that URL shape.
export function PlateViewerPage() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()

  const { data: plate, isLoading, isError } = useQuery({
    queryKey: ['b-plate', id],
    queryFn: () => getPlate(id!).then(r => r.data),
    enabled: !!id,
  })

  const [runInputs, setRunInputs] = useState<Record<string, string> | null>(null)

  const handleParamUpdate = (updates: Record<string, string>) => {
    setRunInputs(prev => {
      if (!prev) return prev
      const merged = { ...prev, ...updates }
      return JSON.stringify(merged) === JSON.stringify(prev) ? prev : merged
    })
  }

  const resolveInputs = (params: BPlateInputParam[]): Record<string, string> => {
    const result: Record<string, string> = {}
    for (const p of params) {
      const val = p.fixedValue ?? searchParams.get(p.name) ?? ''
      if (val) result[p.name] = val
    }
    return result
  }

  useEffect(() => {
    setRunInputs(null)
  }, [id])

  useEffect(() => {
    if (!plate || runInputs !== null) return
    setRunInputs(resolveInputs(plate.inputParams))
  }, [plate, id]) // eslint-disable-line react-hooks/exhaustive-deps

  const setChrome = useSetPlateChrome()
  useEffect(() => {
    if (!plate || !runInputs) return
    setChrome({ headerTitle: plate.headerTitle, globalButtons: plate.globalButtons ?? [], runInputs })
    return () => setChrome(null)
  }, [plate, runInputs, setChrome])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 size={24} className="animate-spin text-ink-dim" />
      </div>
    )
  }

  if (isError || !plate) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="flex items-center gap-2 text-sm text-red-500">
          <AlertCircle size={16} /> Failed to load dashboard
        </div>
      </div>
    )
  }

  return (
    <div>
      {runInputs ? (
        <div className={plate.width ? `${plate.width} mx-auto` : undefined}>
          <PlateLayout plate={plate} runInputs={runInputs} onParamUpdate={handleParamUpdate} />
        </div>
      ) : (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={20} className="animate-spin text-ink-dim" />
        </div>
      )}
    </div>
  )
}
